import { supabase } from '../lib/supabase'
import type {
  ZendeskTicketDetail,
  ZendeskTicketHeader,
  ZendeskTicketListParams,
  ZendeskTicketUser,
} from '../types/zendeskTickets'

const BATCH_SIZE = 1000
const IN_CHUNK = 100
const USER_CHUNK = 50

export const zendeskTicketsService = {
  async listByFilters(params: ZendeskTicketListParams): Promise<ZendeskTicketHeader[]> {
    const { dataInicio, dataFim, status, channel, subject, ticketId, identity } = params

    let query = supabase
      .from('zendesk_ticket_list_with_users')
      .select('*')
      .gte('created_at', new Date(`${dataInicio}T00:00:00`).toISOString())
      .lte('created_at', new Date(`${dataFim}T23:59:59`).toISOString())

    if (status) query = query.eq('status', status)
    if (channel) query = query.eq('channel', channel)
    if (subject) query = query.ilike('subject', `%${subject}%`)
    if (identity) query = query.eq('identity', identity)
    if (ticketId) query = query.ilike('ticket_id', `%${ticketId}%`)

    query = query.order('created_at', { ascending: false })

    const allData: ZendeskTicketHeader[] = []
    let from = 0
    let hasMore = true

    while (hasMore) {
      const pagedQuery = query.range(from, from + BATCH_SIZE - 1)
      const { data, error } = await pagedQuery
      if (error) throw error

      if (data && data.length > 0) {
        allData.push(...(data as ZendeskTicketHeader[]))
        if (data.length < BATCH_SIZE) hasMore = false
        else from += BATCH_SIZE
      } else {
        hasMore = false
      }
    }

    return allData
  },

  async listByTicketIds(ticketIds: string[]): Promise<ZendeskTicketHeader[]> {
    const headers: ZendeskTicketHeader[] = []
    for (let i = 0; i < ticketIds.length; i += IN_CHUNK) {
      const chunk = ticketIds.slice(i, i + IN_CHUNK)
      const { data, error } = await supabase
        .from('zendesk_ticket_list')
        .select('*')
        .in('ticket_id', chunk)
      if (error) throw error
      if (data) headers.push(...(data as ZendeskTicketHeader[]))
    }
    return headers
  },

  async getByTicketId(ticketId: string): Promise<ZendeskTicketHeader | null> {
    const { data, error } = await supabase
      .from('zendesk_ticket_list')
      .select('*')
      .eq('ticket_id', ticketId)
      .maybeSingle()
    if (error) throw error
    return (data as ZendeskTicketHeader | null) ?? null
  },

  async getMessagesByTicketId(ticketId: string): Promise<ZendeskTicketDetail[]> {
    const { data, error } = await supabase
      .from('zendesk_ticket_detail')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('date', { ascending: true })
    if (error) throw error
    return (data as ZendeskTicketDetail[]) ?? []
  },

  async getUsersByIds(ids: number[]): Promise<Record<string, { name: string; role: string }>> {
    const usersById: Record<string, { name: string; role: string }> = {}
    if (ids.length === 0) return usersById

    for (let i = 0; i < ids.length; i += USER_CHUNK) {
      const chunk = ids.slice(i, i + USER_CHUNK)
      const { data, error } = await supabase
        .from('zendesk_ticket_users')
        .select('*')
        .in('id', chunk)
      if (error) throw error
      ;(data as ZendeskTicketUser[] | null)?.forEach((user) => {
        usersById[String(user.id)] = { name: user.name, role: user.role }
      })
    }

    return usersById
  },
}
